import { mappingMemory } from '@/features/import/mappingMemory'
import type { CrmField, ImportMode, ParsedFile, RowInput } from './types'

/** Dutch field names (backend `imports/fields.FIELD_LABELS`). */
export const FIELD_LABELS: Record<CrmField, string> = {
  name: 'Naam',
  first_name: 'Voornaam',
  last_name: 'Achternaam',
  email: 'E-mail',
  phone: 'Telefoon',
  job_title: 'Functie',
  organisation: 'Organisatie',
  website: 'Website organisatie',
  kvk_number: 'KvK',
  address: 'Adres',
  postcode: 'Postcode',
  city: 'Plaats',
  linkedin: 'LinkedIn',
  category: 'Categorie',
  labels: 'Labels',
  owner: 'Eigenaar',
  note: 'Notitie',
  newsletter: 'Nieuwsbrief (AVG)',
}

/** The fields in the order of the mapping select. */
export const FIELD_ORDER: readonly CrmField[] = [
  'name',
  'first_name',
  'last_name',
  'email',
  'phone',
  'job_title',
  'organisation',
  'website',
  'kvk_number',
  'address',
  'postcode',
  'city',
  'linkedin',
  'category',
  'labels',
  'owner',
  'note',
  'newsletter',
]

const CONTACT_ONLY: ReadonlySet<CrmField> = new Set<CrmField>([
  'name',
  'first_name',
  'last_name',
  'email',
  'job_title',
  'newsletter',
])
const ORGANISATION_ONLY: ReadonlySet<CrmField> = new Set<CrmField>(['kvk_number', 'address', 'postcode'])
const REPEATABLE: ReadonlySet<CrmField> = new Set<CrmField>(['note'])

/** The fields a file can feed per mode (backend `FIELDS_BY_MODE`). */
export function fieldsFor(mode: ImportMode): { key: CrmField; label: string }[] {
  const excluded = mode === 'contacten' ? ORGANISATION_ONLY : CONTACT_ONLY
  return FIELD_ORDER.filter((field) => !excluded.has(field)).map((field) => ({
    key: field,
    label: FIELD_LABELS[field],
  }))
}

/** Why a mapping cannot be used (Dutch; the same rules as the backend). */
export function mappingProblems(mapping: readonly (CrmField | null)[], mode: ImportMode): string[] {
  const problems: string[] = []
  if (mode === 'contacten') {
    const hasName = mapping.includes('name') || (mapping.includes('first_name') && mapping.includes('last_name'))
    if (!hasName) problems.push('Koppel een kolom aan Naam, of aan Voornaam én Achternaam.')
  } else if (!mapping.includes('organisation')) {
    problems.push('Koppel één kolom aan Organisatie.')
  }
  for (const field of FIELD_ORDER) {
    if (!REPEATABLE.has(field) && mapping.filter((value) => value === field).length > 1) {
      problems.push(`${FIELD_LABELS[field]} is aan meer dan één kolom gekoppeld.`)
    }
  }
  return problems
}

/**
 * The rows as fields: first and last name become one name (a Naam column wins), several note
 * columns become "Kolom: waarde" lines, a column mapped to nothing is never read.
 */
export function applyMapping(
  parsed: Pick<ParsedFile, 'headers' | 'rows' | 'row_numbers'>,
  mapping: readonly (CrmField | null)[],
): RowInput[] {
  const columns = (field: CrmField) => mapping.flatMap((value, index) => (value === field ? [index] : []))
  const noteColumns = columns('note')
  return parsed.rows.map((cells, rowIndex) => {
    const first = (field: CrmField) => {
      const [column] = columns(field)
      return column === undefined ? '' : (cells[column] ?? '').trim()
    }
    const note =
      noteColumns.length === 1
        ? (cells[noteColumns[0]] ?? '').trim()
        : noteColumns
            .map((column) => [parsed.headers[column], (cells[column] ?? '').trim()] as const)
            .filter(([, value]) => value)
            .map(([header, value]) => `${header}: ${value}`)
            .join('\n')
    return {
      row_number: parsed.row_numbers[rowIndex],
      name: first('name') || [first('first_name'), first('last_name')].filter(Boolean).join(' '),
      first_name: '',
      last_name: '',
      email: first('email'),
      phone: first('phone'),
      job_title: first('job_title'),
      organisation: first('organisation'),
      website: first('website'),
      kvk_number: first('kvk_number'),
      address: first('address'),
      postcode: first('postcode'),
      city: first('city'),
      linkedin: first('linkedin'),
      category: first('category'),
      labels: first('labels'),
      owner: first('owner'),
      note,
      newsletter: first('newsletter'),
    }
  })
}

/** Remembered column mappings per mode (a mapping for contacts is no use for organisations). */
export function crmMappingMemory(mode: ImportMode) {
  return mappingMemory<CrmField>(`vonk.crmImport.mapping:${mode}:`, FIELD_ORDER)
}
